import { useState, useRef } from 'react';
import styled from 'styled-components';
import api from '../../../../utils/api'
import { toast } from 'react-toastify';
import { useAccountContext } from '../AdminAccountPage';

// ========================================
// Styled Components - Matching Semantic UI
// ========================================

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 24px 0;
`;

const SubTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 12px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid rgba(34, 36, 38, 0.15);
  margin-bottom: 24px;
  
  th, td {
    text-align: left;
    padding: 11px 12px;
    border-top: 1px solid rgba(34, 36, 38, 0.1);
  }
  
  th {
    background: #f9fafb;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.87);
    border-top: none;
  }
  
  tr:hover td {
    background: #f9fafb;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FileInput = styled.input`
  padding: 8px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  max-width: 400px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  
  ${({ $primary }) => $primary ? `
    background: #2185d0;
    color: white;
    &:hover { background: #1678c2; }
  ` : `
    background: #e0e1e2;
    color: rgba(0, 0, 0, 0.6);
    &:hover { background: #cacbcd; }
  `}
  
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const PreviewSection = styled.div`
  margin-top: 32px;
  padding: 20px;
  background: #f9fafb;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
`;

const PreviewTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 16px 0;
`;

const StatusBadge = styled.span<{ $valid?: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$valid ? '#21ba45' : '#db2828'};
  color: white;
`;

const StatusCount = styled.div`
  margin-bottom: 16px;
  font-size: 14px;
  
  span {
    margin-right: 16px;
  }
`;

const ErrorList = styled.ul`
  margin: 8px 0;
  padding-left: 20px;
  color: #db2828;
  font-size: 13px;
`;

const SuccessMessage = styled.div`
  padding: 16px;
  background: #fcfff5;
  border: 1px solid #a3c293;
  border-radius: 4px;
  color: #2c662d;
  margin-bottom: 16px;
`;

const ResultsSection = styled.div`
  margin-top: 24px;
`;

const ResultsTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 12px 0;
`;

// ========================================
// Types
// ========================================

interface PreviewRow {
  email: string;
  firstName: string;
  lastName: string;
  team: string;
  role: string;
  publicationId: string;
  valid: boolean;
  errors?: string[];
}

interface UploadResult {
  successful: number;
  failed: number;
  successfulRows: PreviewRow[];
  failedRows: PreviewRow[];
}

// ========================================
// Component
// ========================================

function BulkUserUploadTab() {
  const { account } = useAccountContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<PreviewRow[] | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewData(null);
      setUploadResult(null);
    }
  };

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile || !account) return;
    
    setLoading(true);
    setPreviewData(null);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('csv', selectedFile);
      
      const response = await api.post(
        `/admin2/accounts/${account.id}/bulk_user_uploads.json`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      // Parse preview data
      const data = response.data;
      if (data.rows) {
        setPreviewData(data.rows.map((row: any) => ({
          email: row.email,
          firstName: row.first_name || row.firstName,
          lastName: row.last_name || row.lastName,
          team: row.team,
          role: row.role,
          publicationId: String(row.publication_id || row.publicationId),
          valid: row.valid !== false,
          errors: row.errors || [],
        })));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to preview CSV');
    }
    
    setLoading(false);
  };

  const handleConfirmUpload = async () => {
    if (!previewData || !account) return;
    
    setUploading(true);
    
    try {
      const usersPayload: Record<string, any> = {};
      previewData.forEach((row, index) => {
        usersPayload[index] = {
          email: row.email,
          first_name: row.firstName,
          last_name: row.lastName,
          team: row.team,
          role: row.role,
          publication_id: row.publicationId,
        };
      });
      
      const response = await api.put(
        `/admin2/accounts/${account.id}/bulk_user_uploads.json`,
        { users: usersPayload }
      );
      
      const data = response.data;
      setUploadResult({
        successful: data.successful_count || data.successfulCount || 0,
        failed: data.failed_count || data.failedCount || 0,
        successfulRows: data.successful_rows || data.successfulRows || [],
        failedRows: data.failed_rows || data.failedRows || [],
      });
      
      toast.success(`Successfully uploaded ${data.successful_count || 0} users`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload users');
    }
    
    setUploading(false);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = previewData?.filter(r => r.valid).length || 0;
  const invalidCount = previewData?.filter(r => !r.valid).length || 0;

  return (
    <div>
      <SectionTitle>Bulk user upload</SectionTitle>
      
      <SubTitle>Example CSV</SubTitle>
      <Table>
        <thead>
          <tr>
            <th>Email</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Team</th>
            <th>Role</th>
            <th>Publication ID</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>user-to-upload@contently.com</td>
            <td>Firstname</td>
            <td>Lastname</td>
            <td>Contributors</td>
            <td>Contributor</td>
            <td>13</td>
          </tr>
        </tbody>
      </Table>
      
      <form onSubmit={handlePreview}>
        <FormGroup>
          <FileInput
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Button type="submit" $primary disabled={!selectedFile || loading}>
            {loading ? 'Processing...' : 'Preview Upload'}
          </Button>
        </FormGroup>
      </form>
      
      {/* Preview Section */}
      {previewData && !uploadResult && (
        <PreviewSection>
          <PreviewTitle>Preview</PreviewTitle>
          
          <StatusCount>
            <span>Valid: <strong>{validCount}</strong></span>
            <span>Invalid: <strong>{invalidCount}</strong></span>
          </StatusCount>
          
          <Table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Email</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Team</th>
                <th>Role</th>
                <th>Publication ID</th>
              </tr>
            </thead>
            <tbody>
              {previewData.map((row, index) => (
                <tr key={index}>
                  <td>
                    <StatusBadge $valid={row.valid}>
                      {row.valid ? 'Valid' : 'Invalid'}
                    </StatusBadge>
                    {row.errors && row.errors.length > 0 && (
                      <ErrorList>
                        {row.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ErrorList>
                    )}
                  </td>
                  <td>{row.email}</td>
                  <td>{row.firstName}</td>
                  <td>{row.lastName}</td>
                  <td>{row.team}</td>
                  <td>{row.role}</td>
                  <td>{row.publicationId}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          <FormGroup>
            <Button 
              type="button" 
              $primary 
              onClick={handleConfirmUpload}
              disabled={validCount === 0 || uploading}
              style={{ marginRight: 8 }}
            >
              {uploading ? 'Uploading...' : `Confirm Upload (${validCount} users)`}
            </Button>
            <Button type="button" onClick={handleReset}>
              Cancel
            </Button>
          </FormGroup>
        </PreviewSection>
      )}
      
      {/* Results Section */}
      {uploadResult && (
        <ResultsSection>
          <SuccessMessage>
            Upload complete! {uploadResult.successful} users were successfully created.
            {uploadResult.failed > 0 && ` ${uploadResult.failed} users failed.`}
          </SuccessMessage>
          
          {uploadResult.failedRows.length > 0 && (
            <>
              <ResultsTitle>Failed Rows</ResultsTitle>
              <Table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResult.failedRows.map((row, index) => (
                    <tr key={index}>
                      <td>{row.email}</td>
                      <td>
                        <ErrorList>
                          {row.errors?.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ErrorList>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
          
          <FormGroup>
            <Button type="button" $primary onClick={handleReset}>
              Upload Another File
            </Button>
          </FormGroup>
        </ResultsSection>
      )}
    </div>
  );
}

export default BulkUserUploadTab;
